import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Camera,
  CreditCard,
  DollarSign,
  MoreVertical,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  Eye,
  AlertTriangle,
  X,
  Clock,
} from 'lucide-react';
import { Card, StatCard } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { adminApi } from '../services/api';
import { useAuthStore } from '../services/auth';
import type { AdminCustomer } from '../types';

// ---------------------------------------------------------------------------
// Status badge helper
// ---------------------------------------------------------------------------
function statusColor(status: string | undefined) {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'grace_period':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'expired':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
    case 'suspended':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
  }
}

function statusLabel(status: string | undefined) {
  if (!status) return 'None';
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Suspend Modal
// ---------------------------------------------------------------------------
function SuspendModal({
  customer,
  onClose,
  onConfirm,
  isLoading,
}: {
  customer: AdminCustomer;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <ShieldAlert className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Suspend Organization</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
          You are about to suspend{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {customer.organization.name}
          </span>
          . This will immediately restrict their access.
        </p>

        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          Suspension Reason
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. Non-payment, terms violation..."
          className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
        />

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            isLoading={isLoading}
            disabled={!reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            Suspend
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unsuspend Confirmation Modal
// ---------------------------------------------------------------------------
function UnsuspendModal({
  customer,
  onClose,
  onConfirm,
  isLoading,
}: {
  customer: AdminCustomer;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <ShieldCheck className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Unsuspend Organization</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
          Are you sure you want to unsuspend{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {customer.organization.name}
          </span>
          ? Their access will be restored immediately.
        </p>

        {customer.organization.suspension_reason && (
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
              Original suspension reason
            </p>
            <p className="text-sm text-gray-700 dark:text-slate-300">
              {customer.organization.suspension_reason}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" isLoading={isLoading} onClick={onConfirm}>
            Unsuspend
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grant Trial Modal
// ---------------------------------------------------------------------------
function TrialModal({
  customer,
  onClose,
  onConfirm,
  isLoading,
}: {
  customer: AdminCustomer;
  onClose: () => void;
  onConfirm: (data: { days?: number; trial_end_date?: string }) => void;
  isLoading: boolean;
}) {
  const [mode, setMode] = useState<'days' | 'date'>('days');
  const [days, setDays] = useState(30);
  const [endDate, setEndDate] = useState('');

  const existingTrial = customer.subscription?.trial_end;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
            <Clock className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Grant Trial License</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
          Grant a free trial to{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {customer.organization.name}
          </span>
        </p>

        {existingTrial && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300">
              Existing trial expires: {new Date(existingTrial).toLocaleDateString('en-ZA')}
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
              Granting a new trial will override the existing one.
            </p>
          </div>
        )}

        {/* Mode selector */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('days')}
            className={`flex-1 text-sm py-2 rounded-lg border transition-colors ${
              mode === 'days'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
            }`}
          >
            Number of days
          </button>
          <button
            onClick={() => setMode('date')}
            className={`flex-1 text-sm py-2 rounded-lg border transition-colors ${
              mode === 'date'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700'
            }`}
          >
            Specific date
          </button>
        </div>

        {mode === 'days' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Trial duration (days)
            </label>
            <input
              type="number"
              value={days}
              min={1}
              max={365}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-1"
            />
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Trial will expire on{' '}
              {new Date(Date.now() + days * 86400000).toLocaleDateString('en-ZA')}
            </p>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Trial expiry date
            </label>
            <input
              type="date"
              value={endDate}
              min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isLoading={isLoading}
            disabled={mode === 'date' && !endDate}
            onClick={() =>
              onConfirm(
                mode === 'days'
                  ? { days }
                  : { trial_end_date: endDate }
              )
            }
          >
            Grant Trial
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Actions Dropdown
// ---------------------------------------------------------------------------
function ActionsDropdown({
  customer,
  onSuspend,
  onUnsuspend,
  onImpersonate,
  onGrantTrial,
}: {
  customer: AdminCustomer;
  onSuspend: () => void;
  onUnsuspend: () => void;
  onImpersonate: () => void;
  onGrantTrial: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isSuspended = customer.organization.is_suspended;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-400 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg min-w-[180px] py-1">
            <button
              onClick={() => {
                setOpen(false);
                // View details could navigate to a detail page; for now we log
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>

            {isSuspended ? (
              <button
                onClick={() => {
                  setOpen(false);
                  onUnsuspend();
                }}
                className="w-full text-left px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
              >
                <ShieldCheck className="w-4 h-4" />
                Unsuspend
              </button>
            ) : (
              <button
                onClick={() => {
                  setOpen(false);
                  onSuspend();
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
              >
                <ShieldAlert className="w-4 h-4" />
                Suspend
              </button>
            )}

            <button
              onClick={() => {
                setOpen(false);
                onGrantTrial();
              }}
              className="w-full text-left px-4 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              Grant Trial
            </button>

            <button
              onClick={() => {
                setOpen(false);
                onImpersonate();
              }}
              className="w-full text-left px-4 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <UserCog className="w-4 h-4" />
              Impersonate
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function AdminDashboard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setAuth, setOrganization } = useAuthStore();

  const [suspendTarget, setSuspendTarget] = useState<AdminCustomer | null>(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState<AdminCustomer | null>(null);
  const [trialTarget, setTrialTarget] = useState<AdminCustomer | null>(null);

  // ---- Data fetching ----
  const { data: stats } = useQuery({
    queryKey: ['admin', 'dashboard-stats'],
    queryFn: () => adminApi.getDashboardStats(),
  });

  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ['admin', 'customers'],
    queryFn: () => adminApi.listCustomers(),
  });

  // ---- Mutations ----
  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminApi.suspendCustomer(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard-stats'] });
      setSuspendTarget(null);
    },
  });

  const unsuspendMutation = useMutation({
    mutationFn: (id: string) => adminApi.unsuspendCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard-stats'] });
      setUnsuspendTarget(null);
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: (id: string) => adminApi.impersonate(id),
    onSuccess: (data) => {
      // Store impersonation token and switch context
      setAuth({
        access_token: data.access_token,
        refresh_token: '', // impersonation sessions don't have refresh tokens
        user: data.user,
      });
      setOrganization(data.organization);
      // Store a flag so the app can show the impersonation banner
      sessionStorage.setItem('impersonating', 'true');
      navigate('/');
    },
  });

  const trialMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { days?: number; trial_end_date?: string } }) =>
      adminApi.grantTrial(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard-stats'] });
      setTrialTarget(null);
    },
  });

  // ---- Render helpers ----
  const isImpersonating = sessionStorage.getItem('impersonating') === 'true';

  return (
    <div className="space-y-6">
      {/* Impersonation Warning Banner */}
      {isImpersonating && (
        <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300 flex-1">
            You are currently impersonating a customer. All actions will be performed as this
            organization.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              sessionStorage.removeItem('impersonating');
              useAuthStore.getState().logout();
              navigate('/login');
            }}
          >
            End Session
          </Button>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-gray-500 dark:text-slate-400 mt-1">
          Manage customers, subscriptions, and platform health
        </p>
      </div>

      {/* Overview Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Customers"
          value={stats?.total_customers.toLocaleString() ?? '---'}
          icon={<Building2 className="w-6 h-6" />}
        />
        <StatCard
          title="Total Cameras"
          value={stats?.total_cameras.toLocaleString() ?? '---'}
          icon={<Camera className="w-6 h-6" />}
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.active_subscriptions.toLocaleString() ?? '---'}
          icon={<CreditCard className="w-6 h-6" />}
        />
        <StatCard
          title="Monthly Revenue"
          value={stats ? `R${Number(stats.monthly_revenue).toLocaleString()}` : '---'}
          icon={<DollarSign className="w-6 h-6" />}
        />
      </div>

      {/* Subscription Breakdown Badges */}
      {stats && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-500 dark:text-slate-400">
            Subscriptions:
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Active: {stats.subscription_breakdown.active}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
            Grace Period: {stats.subscription_breakdown.grace_period}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Expired: {stats.subscription_breakdown.expired}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-slate-500" />
            Cancelled: {stats.subscription_breakdown.cancelled}
          </span>
        </div>
      )}

      {/* Customer Table */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customers</h2>
        </div>

        {customersLoading ? (
          <div className="px-6 py-12 text-center text-gray-400 dark:text-slate-500">
            Loading customers...
          </div>
        ) : !customers || customers.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-400 dark:text-slate-500">
            No customers found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-slate-700">
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Organization
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Owner Email
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Cameras
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Status
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Trial
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Monthly Amount
                  </th>
                  <th className="text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                {customers.map((customer) => {
                  const isSuspended = customer.organization.is_suspended;
                  return (
                    <tr
                      key={customer.organization.id}
                      className={
                        isSuspended
                          ? 'bg-red-50/50 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors'
                      }
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {customer.organization.name}
                          </span>
                          {isSuspended && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-red-600 text-white">
                              Suspended
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-slate-300">
                          {customer.owner_email}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {customer.camera_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(
                            customer.subscription?.status
                          )}`}
                        >
                          {statusLabel(customer.subscription?.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {customer.subscription?.trial_end ? (
                          <div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              new Date(customer.subscription.trial_end) > new Date()
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-500'
                            }`}>
                              {new Date(customer.subscription.trial_end) > new Date() ? 'Active' : 'Expired'}
                            </span>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-0.5">
                              {new Date(customer.subscription.trial_end).toLocaleDateString('en-ZA')}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-slate-600">---</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {customer.subscription
                            ? `R${Number(customer.subscription.total_amount).toLocaleString()}`
                            : '---'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <ActionsDropdown
                          customer={customer}
                          onSuspend={() => setSuspendTarget(customer)}
                          onUnsuspend={() => setUnsuspendTarget(customer)}
                          onImpersonate={() =>
                            impersonateMutation.mutate(customer.organization.id)
                          }
                          onGrantTrial={() => setTrialTarget(customer)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Suspend Modal */}
      {suspendTarget && (
        <SuspendModal
          customer={suspendTarget}
          onClose={() => setSuspendTarget(null)}
          onConfirm={(reason) =>
            suspendMutation.mutate({ id: suspendTarget.organization.id, reason })
          }
          isLoading={suspendMutation.isPending}
        />
      )}

      {/* Unsuspend Modal */}
      {unsuspendTarget && (
        <UnsuspendModal
          customer={unsuspendTarget}
          onClose={() => setUnsuspendTarget(null)}
          onConfirm={() => unsuspendMutation.mutate(unsuspendTarget.organization.id)}
          isLoading={unsuspendMutation.isPending}
        />
      )}

      {/* Grant Trial Modal */}
      {trialTarget && (
        <TrialModal
          customer={trialTarget}
          onClose={() => setTrialTarget(null)}
          onConfirm={(data) =>
            trialMutation.mutate({ id: trialTarget.organization.id, data })
          }
          isLoading={trialMutation.isPending}
        />
      )}
    </div>
  );
}
